/**
 * Where the presenter panel's agenda list has to scroll so the current item
 * and the few after it are all on screen - pure, so the rules are tested
 * without a browser. The caller measures and scrolls.
 */

/** One agenda item as it is on screen right now. */
export type AgendaItemBox = {
	/** Its top edge in the viewport (`getBoundingClientRect().top`). */
	top: number
	height: number
}

export type AgendaScrollInput = {
	/** The list's visible top edge in the viewport. */
	containerTop: number
	/** The list's visible height (`clientHeight`). */
	containerHeight: number
	/** How far the list is scrolled now. */
	scrollTop: number
	/** Every item, in order, measured at that scroll position. */
	items: AgendaItemBox[]
	/** `-1` while nothing is current. */
	currentIndex: number
	/** How many items after the current one stay in view. */
	lookahead: number
	/** Room kept above the current item when it is lined up with the top. */
	padding: number
}

/** Sub-pixel layout noise is not a reason to scroll. */
const TOLERANCE = 0.5

/**
 * The `scrollTop` that puts the current item and the next `lookahead` items
 * fully in view, or `null` when they already are (or there is nothing to
 * show).
 *
 * - Already in view: no scroll.
 * - Below the view (moving forward): the least scroll, which lines the last
 *   of them up with the bottom edge.
 * - Above the view (moving back): the least scroll that brings the current
 *   item in, with `padding` above it as far as the rest still fits.
 * - Too tall to fit at all: the current item goes to the top, below
 *   `padding` - it is never the one that is hidden.
 * - Near the end, fewer than `lookahead` items follow: the ones there are.
 */
export const agendaScrollTarget = ({
	containerTop,
	containerHeight,
	scrollTop,
	items,
	currentIndex,
	lookahead,
	padding,
}: AgendaScrollInput): number | null => {
	if (
		containerHeight <= 0 ||
		currentIndex < 0 ||
		currentIndex >= items.length
	) {
		return null
	}
	const current = items[currentIndex]
	const last =
		items[Math.min(items.length - 1, currentIndex + Math.max(0, lookahead))]
	const room = Math.max(0, padding)

	// Both ends in the list's own scrolled content, so they do not move as it
	// scrolls.
	const start = current.top - containerTop + scrollTop
	const end = last.top - containerTop + scrollTop + last.height
	const span = end - start

	let target: number
	if (span > containerHeight) {
		target = start - room
	} else if (start < scrollTop - TOLERANCE) {
		target = start - Math.min(room, containerHeight - span)
	} else if (end > scrollTop + containerHeight + TOLERANCE) {
		target = end - containerHeight
	} else {
		return null
	}

	const clamped = Math.max(0, target)
	return Math.abs(clamped - scrollTop) < TOLERANCE ? null : clamped
}
